import type {Caption} from '@remotion/captions';
// Structural data only: do not pull the React 19 editor's alias-dependent model types into the host.
type CaptionAsset={id:string;timelineSourceAssetId?:string;captions:Caption[]};
type CaptionsItem={id?:string;timelineSourceItemId?:string;from:number;durationInFrames:number;captionStartInSeconds:number;pageDurationInMilliseconds:number;captionEdgeHold?:{left?:boolean;right?:boolean}};
import { createTikTokStyleCaptions } from "@remotion/captions";
import {
  splitCaptionsByGap,
  CAPTION_GAP_SPLIT_THRESHOLD_MS,
} from "../vendor/src/features/remotion-editor/editor/items/captions/split-captions-by-gap";

const tokensInWindow = (asset: Pick<CaptionAsset, "captions">, start: number, end: number) =>
  asset.captions.filter(
    (token) =>
      token.endMs > start * 1000 + 0.001 &&
      token.startMs < end * 1000 - 0.001,
  );

const textInWindow = (
  asset: Pick<CaptionAsset, "captions">,
  start: number,
  end: number,
  item?: Pick<CaptionsItem, "captionEdgeHold" | "pageDurationInMilliseconds">,
) => {
  const text = tokensInWindow(asset, start, end)
    .map((token) => token.text)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  if (text || !item?.captionEdgeHold || !asset.captions.length) return text;
  const first = Math.min(...asset.captions.map((token) => token.startMs)),
    last = Math.max(...asset.captions.map((token) => token.endMs));
  const pages = splitCaptionsByGap(
    asset.captions,
    CAPTION_GAP_SPLIT_THRESHOLD_MS,
  ).flatMap(
    (captions) =>
      createTikTokStyleCaptions({
        captions,
        combineTokensWithinMilliseconds: item.pageDurationInMilliseconds,
      }).pages,
  );
  const page =
    item.captionEdgeHold.right && start * 1000 >= last
      ? pages.at(-1)
      : item.captionEdgeHold.left && end * 1000 <= first
        ? pages[0]
        : undefined;
  return page?.text.replace(/\s+/g, " ").trim() ?? text;
};
export function captionWindowText(
  item: Pick<CaptionsItem, "captionStartInSeconds" | "durationInFrames" | "captionEdgeHold" | "pageDurationInMilliseconds">,
  asset: Pick<CaptionAsset, "captions">,
  fps: number,
): string {
  return textInWindow(
    asset,
    item.captionStartInSeconds,
    item.captionStartInSeconds + item.durationInFrames / fps,
    item,
  );
}

/** A timeline cut of one cue must not turn into repeated speech in the transcript. */
export function captionScriptText(
  clips: readonly {
    item: CaptionsItem;
    asset: CaptionAsset;
    trackId?: string;
  }[],
  fps: number,
): string {
  const tokenKey = (token: Caption) => JSON.stringify([token.startMs, token.endMs, token.text]);
  const counts = (tokens: Caption[]) => {
    const result = new Map<string, number>();
    for (const token of tokens) {
      const key = tokenKey(token);
      result.set(key, (result.get(key) ?? 0) + 1);
    }
    return result;
  };
  const parts: Array<{
    origin: string;
    lineage: string | undefined;
    timelineEnd: number;
    sourceStart: number;
    sourceEnd: number;
    tokens: Caption[];
    separators: Set<number>;
    lastVisible: Map<string, number>;
    previousWindowTokens: Caption[];
    suppressed: Set<number>;
    pendingCrossing: {
      original: Caption;
      cursorMs: number;
      text: string;
      indices: number[];
    } | null;
    asset: CaptionAsset;
    item: CaptionsItem;
  }> = [];
  const last = new Map<string, (typeof parts)[number]>();
  for (const { item, asset, trackId } of [...clips].sort(
    (a, b) => a.item.from - b.item.from,
  )) {
    const origin = JSON.stringify([
      trackId,
      asset.timelineSourceAssetId ?? asset.id,
    ]);
    const sourceStart = item.captionStartInSeconds,
      sourceEnd = sourceStart + item.durationInFrames / fps;
    const visible = tokensInWindow(asset, sourceStart, sourceEnd);
    const previous = last.get(origin);
    const lineage=item.timelineSourceItemId??item.id;
    const continuousSource=!!previous&&Math.abs(previous.sourceEnd-sourceStart)<1e-7;
    // Native ripple retains original token clocks while removing source silence.
    // A forward jump is one occurrence only with explicit same item lineage AND
    // the identical original token spanning both retained windows. Rewinds,
    // unknown/different lineage, edited tokens and later token occurrences stay separate.
    const forwardCut=!!previous&&typeof lineage==='string'&&lineage.length>0&&previous.lineage===lineage
      &&sourceStart>previous.sourceEnd+1e-7&&visible.some(token=>
        token.startMs<previous.sourceEnd*1000-0.001&&token.endMs>sourceStart*1000+0.001
        &&(previous.lastVisible.get(tokenKey(token))??0)>0);
    if (
      previous &&
      previous.timelineEnd === item.from &&
      (continuousSource||forwardCut)
    ) {
      // A cut can keep the full crossing token in its first view while Enter
      // turns the remainder in following views into local pieces. Defer their
      // removal until at least two pieces exactly reconstruct that token.
      if(forwardCut)previous.pendingCrossing=null;
      if (continuousSource&&!previous.pendingCrossing && asset.timelineSourceAssetId) {
        const boundaryMs = sourceStart * 1000;
        const crossing = previous.previousWindowTokens.find(token =>
          token.startMs < boundaryMs - 0.001 && token.endMs > boundaryMs + 0.001 &&
          previous.tokens.some(kept => tokenKey(kept) === tokenKey(token)));
        if (crossing) previous.pendingCrossing = {
          original: crossing, cursorMs: boundaryMs, text: '', indices: [],
        };
      }
      const overlap = new Map(previous.lastVisible);
      for (const token of visible) {
        const key = tokenKey(token);
        const remaining = overlap.get(key) ?? 0;
        if (remaining > 0) {
          overlap.set(key, remaining - 1);
          previous.pendingCrossing = null;
        }
        else {
          // An independently edited word in the same source interval is a
          // separate utterance in the script, even if its raw text has no spaces.
          if (previous.tokens.length && (forwardCut||previous.previousWindowTokens.some(prior =>
            prior.endMs > token.startMs + 0.001 && prior.startMs < token.endMs - 0.001))) {
            previous.separators.add(previous.tokens.length);
          }
          const pending = previous.pendingCrossing;
          if (pending) {
            const nextText = pending.text + token.text;
            if (Math.abs(token.startMs - pending.cursorMs) <= 0.001 &&
                token.endMs > token.startMs + 0.001 &&
                token.endMs <= pending.original.endMs + 0.001 &&
                pending.original.text.startsWith(nextText)) {
              pending.indices.push(previous.tokens.length);
              pending.cursorMs = token.endMs;
              pending.text = nextText;
              if (Math.abs(pending.cursorMs - pending.original.endMs) <= 0.001) {
                if (pending.indices.length >= 2 && pending.text === pending.original.text)
                  pending.indices.forEach(index => previous.suppressed.add(index));
                previous.pendingCrossing = null;
              }
            } else previous.pendingCrossing = null;
          }
          previous.tokens.push(token);
        }
      }
      previous.lineage=lineage;
      previous.timelineEnd = item.from + item.durationInFrames;
      previous.sourceEnd = sourceEnd;
      previous.lastVisible = counts(visible);
      previous.previousWindowTokens = visible;
      previous.asset = asset;
      previous.item = item;
    } else {
      const part = {
        origin,
        lineage,
        timelineEnd: item.from + item.durationInFrames,
        sourceStart,
        sourceEnd,
        tokens: [...visible],
        separators: new Set<number>(),
        lastVisible: counts(visible),
        previousWindowTokens: visible,
        suppressed: new Set<number>(),
        pendingCrossing: null,
        asset,
        item,
      };
      parts.push(part);
      last.set(origin, part);
    }
  }
  return parts
    .map((part) => part.tokens.length
      ? part.tokens.map((token,index) => part.suppressed.has(index)
        ? '' : `${part.separators.has(index) ? ' ' : ''}${token.text}`).join("").replace(/\s+/g, " ").trim()
      : textInWindow(part.asset, part.sourceStart, part.sourceEnd, part.item))
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
